// The simulated world: aerodrome, weather, traffic, radio, controllers (player and AI), safety monitoring and
// scoring. Pure TypeScript without DOM access – the UI reads snapshots and sends commands.
import type { AirportMeta, Role, SidMeta, UnitMeta } from '../data/types';
import { Airport, type HoldPoint, type RunwayEnd } from './airport';
import { Aircraft, type Env } from './aircraft';
import { typeOf, type Wake } from './perf';
import type { FlightPlan } from './flightplan';
import { Pilot, type RequestKind } from './pilot';
import type { Cmd, Transmission } from './commands';
import { Rng, hashStr } from './rng';
import { NM, dist, bearing, norm360, angDiff, DEG, type XY } from './geo';
import { makeWeather, chooseConfig, transitionLevel, windAt, atis, lvpActive, nextAtisLetter, type Weather, type WeatherPreset, type AtisInfo } from './weather';
import { atcTransmission, saySid as saySidName, type Phrase, type PhraseCtx } from '../phraseology/messages';
import { DESTINATIONS } from '../data/destinations';
import { AiControllers } from './ai';
import { SafetyMonitor } from './safety';
import { Score, type SimEvent, type Severity } from './scoring';

export interface Assists {
  /** Instructor hints in the event log. */
  hints: boolean;
  /** Predicted conflicts are highlighted. */
  conflictPreview: boolean;
  /** Suggested taxi routes / clearances in the menus. */
  suggestions: boolean;
  /** Time acceleration and pause allowed. */
  timeControl: boolean;
}

export interface SpawnSpec {
  t: number;
  kind: 'DEP' | 'ARR';
  fp: FlightPlan;
  /** For arrivals: distance before the entry fix (NM) at which the aircraft appears. */
  startNm?: number;
  /** For arrivals: appear established on the ILS at this distance (NM). */
  onFinalNm?: number;
}

export interface WorldOptions {
  meta: AirportMeta;
  airport: Airport;
  seed: number;
  startUtc: Date;
  weather: WeatherPreset | Weather;
  configId?: string;
  playerRoles: Role[];
  readbackErrorRate: number;
  assists: Assists;
  spawns: SpawnSpec[];
  /** Sim seconds the session lasts (for the score). */
  duration: number;
}

export interface Request { id: number; cs: string; unit: string; kind: RequestKind; t: number; text: string }

let reqId = 1;
let txId = 1;

export class World {
  t = 0;
  readonly rng: Rng;
  readonly meta: AirportMeta;
  readonly airport: Airport;
  weather: Weather;
  config: { id: string; dep: string[]; arr: string[]; name: string };
  atis: AtisInfo;
  tl: number;
  lvp: boolean;
  readonly aircraft: Aircraft[] = [];
  readonly pilots = new Map<string, Pilot>();
  readonly gone: Aircraft[] = [];
  readonly log: Transmission[] = [];
  readonly requests: Request[] = [];
  readonly events: SimEvent[] = [];
  readonly score: Score;
  readonly ai: AiControllers;
  readonly safety: SafetyMonitor;
  readonly playerRoles: Set<Role>;
  readonly options: WorldOptions;
  readonly standOcc = new Map<string, string>();
  private spawns: SpawnSpec[];
  private freqBusy = new Map<string, number>();
  private pendingTx: { tx: Transmission; kind: RequestKind | null; pilot?: Pilot; cmds?: Cmd[]; byPlayer?: boolean; earliest: number }[] = [];
  private activeTx: { tx: Transmission; end: number; kind: RequestKind | null; pilot?: Pilot; cmds?: Cmd[]; byPlayer?: boolean }[] = [];
  private listeners = { tx: [] as ((t: Transmission) => void)[], ev: [] as ((e: SimEvent) => void)[] };
  private radarT = 0;
  private squawkNext: number;
  readonly cmdLog: { t: number; cs: string; cmds: Cmd[] }[] = [];
  lastDeparture: { t: number; wake: Wake; rwy: string; exit?: string; cs: string } | null = null;

  constructor(opts: WorldOptions) {
    this.options = opts;
    this.meta = opts.meta;
    this.airport = opts.airport;
    this.rng = new Rng(opts.seed);
    this.weather = typeof opts.weather === 'string' ? makeWeather(this.meta, opts.weather, this.rng.fork(1), opts.configId) : opts.weather;
    const cfg = opts.configId ? this.meta.configs.find(c => c.id === opts.configId)! : chooseConfig(this.meta, this.weather, e => this.airport.end(e).hdg);
    this.config = { id: cfg.id, dep: cfg.dep, arr: cfg.arr, name: cfg.name };
    this.tl = transitionLevel(this.meta, this.weather.qnh);
    this.lvp = lvpActive(this.weather);
    this.atis = atis(this.meta, this.weather, cfg, this.rng.pick([...'ABCDEFGHIJKLMNOPQRSTUVW']), opts.startUtc, this.tl, this.lvp);
    this.playerRoles = new Set(opts.playerRoles);
    this.spawns = [...opts.spawns].sort((a, b) => a.t - b.t);
    this.squawkNext = this.meta.squawkRange[0];
    this.score = new Score(this);
    this.ai = new AiControllers(this);
    this.safety = new SafetyMonitor(this);
  }

  // ===================================================================== time
  utc(): Date { return new Date(this.options.startUtc.getTime() + this.t * 1000); }

  tick(dt: number): void {
    this.t += dt;
    while (this.spawns.length && this.spawns[0]!.t <= this.t) this.spawn(this.spawns.shift()!);
    this.updateRadio();
    for (const p of this.pilots.values()) p.update(dt);
    this.groundTraffic();
    const env = this.env();
    for (const ac of this.aircraft) ac.update(dt, env);
    this.ai.update(dt);
    this.safety.update(dt);
    this.radarUpdate();
    this.cleanup();
  }

  env(): Env {
    return {
      airport: this.airport,
      wind: alt => windAt(this.weather, alt),
      qnh: this.weather.qnh,
      fix: name => this.fixPos(name),
    };
  }

  // ===================================================================== units and roles
  unit(id: string): UnitMeta {
    const u = this.meta.units.find(x => x.id === id);
    if (!u) throw new Error(`unknown unit ${id}`);
    return u;
  }
  unitCallsign(id: string): string { return this.unit(id).callsign; }
  unitFreq(id: string): string { return this.unit(id).freq; }
  unitRoles(id: string): Role[] { return this.unit(id).roles; }
  /** Unit that performs a function at this airport. */
  unitFor(role: Role): string | null { return this.meta.stages[role] ?? this.meta.units.find(u => u.roles.includes(role))?.id ?? null; }
  isPlayerRole(role: Role): boolean { return this.playerRoles.has(role); }
  /** Units the player works (all units of the player's roles). */
  playerUnits(): string[] { return [...new Set([...this.playerRoles].map(r => this.unitFor(r)).filter((u): u is string => !!u))]; }

  // ===================================================================== data helpers
  fixPos(name: string): XY | null {
    const f = this.meta.fixes[name];
    if (f) return this.airport.proj.toXY(f.lat, f.lon);
    const syn = this.syntheticFixes.get(name);
    return syn ?? null;
  }
  readonly syntheticFixes = new Map<string, XY>();
  standSay(id?: string): string { return id ? this.airport.stands[id]?.say ?? `stand ${id}` : 'parking'; }
  destName(icao: string): string { return DESTINATIONS[icao]?.sayAs ?? icao; }
  fixSay(name: string): string { return this.meta.fixes[name]?.say ?? name; }
  saySid(sid: string): string { return saySidName(sid, { fixSay: n => this.fixSay(n) }); }
  sidOf(name?: string): SidMeta | undefined { return name ? this.meta.sids.find(s => s.name === name) : undefined; }
  magToTrue(mag: number): number { return norm360(mag + this.meta.magVar); }
  trueToMag(tr: number): number { return norm360(tr - this.meta.magVar); }
  runwayIdOf(endId: string): string { return this.airport.runwayEnds[endId]?.runway.id ?? endId; }
  ac(cs: string): Aircraft | undefined { return this.aircraft.find(a => a.cs === cs); }
  pilot(cs: string): Pilot | undefined { return this.pilots.get(cs); }

  phraseCtx(): PhraseCtx {
    return {
      unitCallsign: id => this.unitCallsign(id),
      unitFreq: id => this.unitFreq(id),
      destName: icao => this.destName(icao),
      fixSay: n => this.fixSay(n),
      standSay: id => this.standSay(id),
      windFor: () => {
        const w = this.weather;
        return { dir: this.trueToMag(w.windDir), kt: w.windKt, gust: w.gustKt };
      },
      qnh: this.weather.qnh,
      currentAlt: cs => { const a = this.ac(cs); return a ? (a.altFt > this.meta.transitionAltFt ? a.pressureAlt(this.weather.qnh) : a.altFt) : null; },
      currentSpeed: cs => { const a = this.ac(cs); return a && !a.onGround ? a.ias : null; },
    };
  }

  nextSquawk(): string {
    const [lo, hi] = this.meta.squawkRange;
    for (let i = 0; i <= hi - lo; i++) {
      const c = this.squawkNext;
      this.squawkNext = c >= hi ? lo : c + 1;
      const code = c.toString(8).padStart(4, '0');
      if (!/[89]/.test(code) && !this.aircraft.some(a => a.squawk === code)) return code;
    }
    return '7000';
  }

  // ===================================================================== spawning
  private spawn(s: SpawnSpec): void {
    const type = typeOf(s.fp.type);
    const ac = new Aircraft(s.fp, type);
    const pilot = new Pilot(ac, this);
    pilot.atisLetter = this.atis.letter;
    if (s.kind === 'DEP') {
      const st = s.fp.stand ? this.airport.stands[s.fp.stand] : null;
      if (!st || this.standOcc.has(st.id)) return;
      ac.placeAtStand(st, this.airport);
      ac.altFt = this.meta.elevFt;
      this.standOcc.set(st.id, ac.cs);
      pilot.freq = this.unitFor('DEL');
      pilot.depRwy = this.config.dep[0]!;
      pilot.nextCall = { kind: 'CLEARANCE', at: this.t + this.rng.range(5, 40) };
      ac.squawk = '2000';
      s.fp.squawk = this.nextSquawk();
    } else if (s.onFinalNm !== undefined) {
      // already established on the ILS (session starts with traffic in progress)
      const end = this.airport.end(this.config.arr[0]!);
      const ils = end.meta.ils!;
      const back = ils.course + 180;
      const d = s.onFinalNm * NM;
      ac.x = end.thr.x + Math.sin(back * DEG) * d;
      ac.y = end.thr.y + Math.cos(back * DEG) * d;
      ac.onGround = false; ac.gmode = 'TAKEOFF';
      ac.hdg = ils.course; ac.trk = ils.course;
      ac.altFt = end.elevFt + 50 + Math.tan(ils.gp * DEG) * d / 0.3048;
      ac.tgtAltFt = Math.min(3000, ac.altFt);
      ac.ias = s.onFinalNm > 6 ? 170 : type.vapp + 10;
      ac.clearIls(end); ac.locCap = true; ac.gsCap = true; ac.amode = 'LOC';
      ac.squawk = this.nextSquawk();
      pilot.stage = this.isPlayerRole('TWR') || s.onFinalNm < 9 ? 'FINAL' : 'ARRIVING';
      pilot.arrRwy = end.id;
      pilot.freq = pilot.stage === 'FINAL' ? this.unitFor('TWR') : this.unitFor('APP');
      pilot.nextCall = { kind: 'CHECKIN_FINAL', at: this.t + this.rng.range(2, 10) };
      if (s.fp.stand) this.standOcc.set(s.fp.stand, ac.cs);
      ac.radar = { x: ac.x, y: ac.y, alt: ac.altFt, gs: ac.ias, trk: ac.trk, t: this.t };
    } else {
      const star = this.meta.stars.find(x => x.entry === s.fp.entryFix) ?? this.meta.stars[0]!;
      const entry = this.fixPos(star.entry)!;
      const iaf = this.fixPos(star.iaf)!;
      // start a bit before the entry fix, on the inbound track towards the IAF
      const brg = bearing(entry.x, entry.y, iaf.x, iaf.y);
      const back = (s.startNm ?? 6) * NM;
      ac.x = entry.x - Math.sin(brg * DEG) * back;
      ac.y = entry.y - Math.cos(brg * DEG) * back;
      ac.onGround = false;
      ac.gmode = 'TAKEOFF';
      ac.hdg = brg; ac.trk = brg;
      const startFl = Math.min(type.typicalFL, 110);
      ac.altFt = startFl * 100 - (1013.25 - this.weather.qnh) * 27.3;
      ac.tgtAltFt = ac.altFt;
      ac.ias = Math.min(type.descIas, 280);
      ac.directTo(star.legs[0]!.fix, star.legs.map(l => l.fix));
      ac.squawk = this.nextSquawk();
      pilot.stage = 'ARRIVING';
      pilot.arrRwy = this.config.arr[0]!;
      const ctr = this.unitFor('CTR');
      pilot.freq = ctr ?? this.unitFor('APP');
      pilot.nextCall = { kind: 'CHECKIN_ARR', at: this.t + this.rng.range(2, 8) };
      if (s.fp.stand) this.standOcc.set(s.fp.stand, ac.cs);
      // keep the last known ATIS letter of the crew (may be outdated → the controller has to notice)
      if (this.rng.chance(0.15)) pilot.atisLetter = String.fromCharCode(Math.max(65, this.atis.letter.charCodeAt(0) - 1));
      ac.radar = { x: ac.x, y: ac.y, alt: ac.altFt, gs: ac.ias, trk: ac.trk, t: this.t };
    }
    this.aircraft.push(ac);
    this.pilots.set(ac.cs, pilot);
    this.score.movementStarted(ac);
  }

  /** Adds a scheduled spawn later (used by events, e.g. a go-around re-entering the sequence). */
  addSpawn(s: SpawnSpec): void { this.spawns.push(s); this.spawns.sort((a, b) => a.t - b.t); }
  get pendingSpawns(): readonly SpawnSpec[] { return this.spawns; }

  private cleanup(): void {
    for (let i = this.aircraft.length - 1; i >= 0; i--) {
      const ac = this.aircraft[i]!;
      const p = this.pilots.get(ac.cs)!;
      const far = !ac.onGround && dist(ac.x, ac.y, 0, 0) > 95 * NM;
      const parkedLong = p.stage === 'PARKED' && this.t - p.parkedAt > 90;
      const departedOut = p.isDeparture && !ac.onGround && (dist(ac.x, ac.y, 0, 0) > 60 * NM || (ac.altFt > 20000 && !this.isPlayerRole('CTR')));
      if (far || parkedLong || departedOut) {
        if (departedOut || far) this.score.movementCompleted(ac, p);
        p.stage = 'GONE';
        this.resolveRequestsOf(ac.cs);
        this.aircraft.splice(i, 1);
        this.gone.push(ac);
        this.pilots.delete(ac.cs);
        if (ac.stand && p.isDeparture) this.standOcc.delete(ac.stand.id);
      }
    }
  }

  // ===================================================================== radio
  onTransmission(fn: (t: Transmission) => void): () => void { this.listeners.tx.push(fn); return () => { this.listeners.tx = this.listeners.tx.filter(f => f !== fn); }; }
  onEvent(fn: (e: SimEvent) => void): () => void { this.listeners.ev.push(fn); return () => { this.listeners.ev = this.listeners.ev.filter(f => f !== fn); }; }

  private duration(text: string, atc: boolean): number {
    const words = text.split(/\s+/).filter(Boolean).length;
    return 0.5 + words / (atc ? 3.1 : 2.8);
  }

  pilotTransmit(p: Pilot, ph: Phrase, kind: RequestKind | null, rbError = false): void {
    if (!p.freq) return;
    const tx: Transmission = { id: txId++, t: 0, dur: this.duration(ph.spoken, false), freq: p.freq, from: 'PILOT', cs: p.cs, text: ph.text, spoken: ph.spoken, rbError, voice: p.cs };
    this.pendingTx.push({ tx, kind, pilot: p, earliest: this.t + this.rng.range(0.3, 1.5) });
  }

  /** Controller transmission (player or AI). Delivered to the pilot when it ends. */
  atcTransmit(unit: string, cs: string, cmds: Cmd[], byPlayer: boolean, station?: string): Transmission | null {
    const ac = this.ac(cs);
    if (!ac) return null;
    const ph = atcTransmission(ac.fp, cmds, this.phraseCtx(), station);
    const tx: Transmission = { id: txId++, t: 0, dur: this.duration(ph.spoken, true), freq: unit, from: 'ATC', cs, text: ph.text, spoken: ph.spoken, byPlayer, voice: `ATC:${unit}` };
    this.pendingTx.unshift({ tx, kind: null, cmds, byPlayer, earliest: this.t });
    if (byPlayer) this.cmdLog.push({ t: this.t, cs, cmds });
    return tx;
  }

  /** The player's instruction. Returns a German warning if it cannot work. */
  playerIssue(cs: string, cmds: Cmd[]): string | null {
    const ac = this.ac(cs);
    if (!ac) return `${cs} ist nicht (mehr) im Verkehr.`;
    const p = this.pilots.get(cs)!;
    const units = this.playerUnits();
    const unit = p.freq && units.includes(p.freq) ? p.freq : units[0]!;
    this.atcTransmit(unit, cs, cmds, true);
    this.score.playerCommand(ac, cmds);
    if (!p.freq || !units.includes(p.freq)) {
      if (this.options.assists.hints) this.event('comms', 'info', 'NOT_ON_FREQ', `${cs} ist nicht auf deiner Frequenz – keine Antwort zu erwarten.`, cs, 0);
      else this.event('comms', 'minor', 'NOT_ON_FREQ', `Funkspruch an ${cs}, das nicht auf deiner Frequenz ist.`, cs);
      return `${cs} hört nicht auf deiner Frequenz.`;
    }
    return null;
  }

  frequencyBusy(unit: string): boolean { return (this.freqBusy.get(unit) ?? 0) > this.t; }

  /** A controller transmission to this aircraft is queued, being transmitted or the pilot has not reacted yet. */
  awaitingReaction(cs: string): boolean {
    if (this.pendingTx.some(q => q.tx.from === 'ATC' && q.tx.cs === cs)) return true;
    if (this.activeTx.some(q => q.tx.from === 'ATC' && q.tx.cs === cs)) return true;
    return this.pilots.get(cs)?.reacting ?? false;
  }

  private updateRadio(): void {
    // end transmissions
    for (const a of [...this.activeTx]) {
      if (a.end > this.t) continue;
      this.activeTx = this.activeTx.filter(x => x !== a);
      if (a.tx.from === 'ATC' && a.cmds) this.pilots.get(a.tx.cs)?.receive(a.cmds, a.tx.freq, !!a.byPlayer);
      if (a.tx.from === 'PILOT' && a.pilot && a.pilot.freq === a.tx.freq) a.pilot.contacted = true;
      // the request stays open only while the pilot is still waiting for an answer
      if (a.tx.from === 'PILOT' && a.kind && a.pilot?.waitingFor?.kind === a.kind) this.addRequest(a.tx.cs, a.tx.freq, a.kind, a.tx.text);
    }
    // start queued transmissions on free frequencies (ATC first)
    for (const q of [...this.pendingTx]) {
      if (q.earliest > this.t) continue;
      const f = q.tx.freq;
      if ((this.freqBusy.get(f) ?? 0) > this.t) continue;
      if (q.pilot && q.pilot.freq !== f) { this.pendingTx = this.pendingTx.filter(x => x !== q); continue; }
      this.pendingTx = this.pendingTx.filter(x => x !== q);
      q.tx.t = this.t;
      this.freqBusy.set(f, this.t + q.tx.dur + 0.4);
      this.activeTx.push({ tx: q.tx, end: this.t + q.tx.dur, kind: q.kind, pilot: q.pilot, cmds: q.cmds, byPlayer: q.byPlayer });
      this.log.push(q.tx);
      if (this.log.length > 600) this.log.splice(0, this.log.length - 600);
      for (const fn of this.listeners.tx) fn(q.tx);
    }
  }

  private addRequest(cs: string, unit: string, kind: RequestKind, text: string): void {
    if (['SAY_AGAIN'].includes(kind)) return;
    const old = this.requests.findIndex(r => r.cs === cs && r.kind === kind);
    if (old >= 0) this.requests.splice(old, 1);
    this.requests.push({ id: reqId++, cs, unit, kind, t: this.t, text });
  }
  resolveRequest(cs: string, kind: RequestKind): void {
    const i = this.requests.findIndex(r => r.cs === cs && r.kind === kind);
    if (i >= 0) { this.score.requestAnswered(this.requests[i]!); this.requests.splice(i, 1); }
  }
  resolveRequestsOf(cs: string): void {
    for (let i = this.requests.length - 1; i >= 0; i--) if (this.requests[i]!.cs === cs) this.requests.splice(i, 1);
  }
  requestsFor(unit: string): Request[] { return this.requests.filter(r => r.unit === unit); }

  // ===================================================================== events (scoring)
  event(kind: SimEvent['kind'], sev: Severity, code: string, text: string, cs?: string, points?: number): void {
    const e = this.score.add({ t: this.t, kind, sev, code, text, cs, points });
    if (!e) return;
    this.events.push(e);
    for (const fn of this.listeners.ev) fn(e);
  }
  readbackErrorMade(cs: string, right: Cmd, wrong: Cmd): void { this.score.readbackError(cs, right, wrong); }
  readbackCorrected(cs: string, caught: boolean): void {
    if (caught) { this.score.caught(); this.event('comms', 'info', 'RB_CAUGHT', `Falsches Readback von ${cs} erkannt und korrigiert.`, cs, 4); }
    else this.event('comms', 'major', 'RB_MISSED', `Falsches Readback von ${cs} nicht korrigiert – der Pilot fliegt/rollt mit dem falschen Wert.`, cs);
  }
  unanswered(cs: string, kind: RequestKind, unit: string): void {
    if (this.playerUnits().includes(unit) && this.isPlayerUnitRole(unit, kind)) this.event('comms', 'minor', 'UNANSWERED', `${cs} musste seinen Anruf wiederholen (keine Antwort).`, cs);
  }
  private isPlayerUnitRole(unit: string, kind: RequestKind): boolean {
    const role = requestRole(kind);
    if (!role) return this.unitRoles(unit).some(r => this.isPlayerRole(r));
    return this.isPlayerRole(role) && this.unitFor(role) === unit;
  }
  goAroundHappened(p: Pilot, own: boolean): void {
    this.score.goAround(p, own);
    if (own) {
      const towerByPlayer = this.isPlayerRole('TWR');
      this.event(towerByPlayer ? 'efficiency' : 'info', towerByPlayer ? 'major' : 'info', 'GA_NO_CLR', `${p.cs} startet durch – keine Landefreigabe erhalten.`, p.cs);
    }
    this.ai.onGoAround(p);
  }
  takeoffStarted(p: Pilot): void {
    const ac = p.ac;
    this.safety.onTakeoff(p);
    this.lastDeparture = { t: this.t, wake: ac.type.wake, rwy: p.depRwy ?? '', exit: p.clearance?.sid, cs: ac.cs };
  }
  vacated(p: Pilot): void { this.score.landed(p); }
  parked(p: Pilot): void { this.score.movementCompleted(p.ac, p); }

  // ===================================================================== ground operations
  /** Plans the pushback: back along the stand lead-in line onto the taxilane, nose towards the taxi route. */
  startPushback(p: Pilot, facing?: string): void {
    const ac = p.ac, ap = this.airport, st = ac.stand;
    if (!st) return;
    const exit = st.apronExit;
    const lead = [...st.lead];
    // where will the aircraft taxi afterwards? Route to the departure holding point decides the facing.
    const hold = this.chooseDepHold(p);
    const route = hold ? ap.findPath(exit, hold.node, { span: ac.type.spanM, operator: ac.fp.airline, stands: [] }) : null;
    const next = route && route.length > 1 ? route[1]! : null;
    const neighbours = ap.adj[exit]!.filter(l => l.e.kind !== 'stand' && !lead.includes(l.to));
    let tail: number | null = null;
    if (facing) {
      const want = { north: 0, east: 90, south: 180, west: 270 }[facing.toLowerCase()] ?? null;
      if (want !== null) {
        const best = neighbours.map(l => ({ l, d: Math.abs(angDiff(bearing(ap.nodes[l.to]!.x, ap.nodes[l.to]!.y, ap.nodes[exit]!.x, ap.nodes[exit]!.y), want)) })).sort((a, b) => a.d - b.d)[0];
        tail = best?.l.to ?? null;
      }
    }
    if (tail === null && next !== null) {
      const ex = ap.nodes[exit]!, nx = ap.nodes[next]!;
      const fwd = bearing(ex.x, ex.y, nx.x, nx.y);
      const best = neighbours.map(l => ({ l, d: Math.abs(angDiff(bearing(ex.x, ex.y, ap.nodes[l.to]!.x, ap.nodes[l.to]!.y), fwd + 180)) })).sort((a, b) => a.d - b.d)[0];
      tail = best?.l.to ?? null;
    }
    const path = lead.slice(1); // from the first node behind the stop position to the apron exit
    if (tail !== null) {
      const ex = ap.nodes[exit]!, tn = ap.nodes[tail]!;
      const d = dist(ex.x, ex.y, tn.x, tn.y);
      const k = Math.min(1, (ac.type.lengthM * 0.55 + 10) / Math.max(1, d));
      const end = { x: ex.x + (tn.x - ex.x) * k, y: ex.y + (tn.y - ex.y) * k };
      ac.setPath(ap, path);
      ac.pts.push(end);
    } else ac.setPath(ap, path);
    ac.gmode = 'PUSHBACK';
    this.standOcc.delete(st.id);
    ac.stand = st;
  }

  /** Departure holding point the pilot expects: full length unless the type prefers an intersection. */
  chooseDepHold(p: Pilot): HoldPoint | null {
    if (p.depHold) return p.depHold;
    const end = this.airport.runwayEnds[p.depRwy ?? this.config.dep[0]!];
    if (!end) return null;
    const need = p.ac.type.todM;
    const holds = end.depHolds.filter(h => this.airport.edgeAllowed({ id: -1, a: 0, b: 0, kind: 'twy', name: h.name, len: 0 }, p.ac.type.spanM));
    // turboprops over 2 t (Bremen AD 2.20) and light aircraft use the first suitable intersection
    const prop = p.ac.type.engine !== 'jet';
    const cand = prop ? [...holds].reverse().find(h => h.tora[end.id]! >= need * 1.1) : holds[0];
    return cand ?? holds[0] ?? null;
  }

  /** Validates (dry run) or executes a taxi instruction. */
  planTaxi(p: Pilot, c: Extract<Cmd, { k: 'TAXI' }>, dryRun: boolean): boolean {
    const ac = p.ac, ap = this.airport;
    let target: number | null = null;
    let hold: HoldPoint | null = null;
    if (c.target.kind === 'HOLD') {
      const end = ap.runwayEnds[c.target.rwy];
      if (!end) return false;
      hold = ap.depHold(c.target.rwy, c.target.hold);
      if (!hold) return false;
      target = this.lvp && hold.catHold !== undefined ? hold.catHold : hold.node;
    } else if (c.target.kind === 'STAND') {
      const st = ap.stands[c.target.stand];
      if (!st) return false;
      target = st.node;
    } else target = c.target.node;
    const start = this.groundStartNode(ac);
    if (start === null || target === null) return false;
    const stands = [ac.stand?.id, c.target.kind === 'STAND' ? c.target.stand : undefined].filter((s): s is string => !!s);
    const extra = hold ? [hold.name] : [];
    const planned = this.plannedPaths.get(ac.cs);
    let path = planned && planned[0] === start && planned[planned.length - 1] === target ? planned : ap.findPath(start, target, { via: c.via, span: ac.type.spanM, operator: ac.fp.airline, stands, extraNames: extra });
    if (!path && c.via.length) return false;
    if (!dryRun) this.plannedPaths.delete(ac.cs);
    if (!path) path = ap.findPath(start, target, { span: ac.type.spanM, operator: ac.fp.airline, stands });
    if (!path) return false;
    if (dryRun) return true;
    // stops in front of every runway the route enters (crossing needs a separate clearance)
    const stops = new Map<number, string>();
    for (const cr of ap.runwayCrossings(path)) {
      if (p.crossings.has(cr.runway.endIds[0]) || p.crossings.has(cr.runway.endIds[1])) continue;
      let si = cr.index - 1;
      for (let k = cr.index - 1; k >= Math.max(0, cr.index - 12); k--) {
        const h = ap.nodes[path[k]!]!.hold;
        if (h && h.runway === cr.runway && h.type === 'runway') { si = k; break; }
      }
      stops.set(si, `RWY:${cr.runway.id}`);
    }
    if (c.holdShort) {
      const rw = ap.runwayEnds[c.holdShort]?.runway ?? ap.runways.find(r => r.id === c.holdShort);
      if (rw) for (const cr of ap.runwayCrossings(path)) if (cr.runway === rw) stops.set(Math.max(0, cr.index - 1), `RWY:${rw.id}`);
    }
    ac.setPath(ap, path, stops);
    ac.holdPosition = false;
    if (hold) { p.depHold = hold; p.depRwy = c.target.kind === 'HOLD' ? c.target.rwy : p.depRwy; }
    if (c.target.kind === 'STAND') ac.fp.stand = c.target.stand;
    return true;
  }

  /** Graph node from which a ground route starts for this aircraft. */
  groundStartNode(ac: Aircraft): number | null {
    const ap = this.airport;
    if (ac.gmode === 'PARKED' && ac.stand) return ac.stand.node;
    if (ac.path.length && ac.ti > 0) {
      // continue from the next node of the current path
      const idx = Math.min(ac.path.length - 1, Math.max(0, ac.ti - 1));
      return ac.path[idx] ?? ap.nearestNode(ac);
    }
    return ap.nearestNode(ac, n => !n.stand);
  }

  /** The clearance the flight data system proposes (SID for the runway and exit, initial climb, assigned code). */
  defaultClearance(p: Pilot): Extract<Cmd, { k: 'CLEARANCE' }> | null {
    const fp = p.ac.fp;
    const rwy = p.depRwy ?? this.config.dep[0]!;
    const sid = this.meta.sids.find(s => s.rwy === rwy && s.exit === fp.exitFix) ?? this.meta.sids.find(s => s.rwy === rwy);
    if (!sid) return null;
    if (!fp.squawk) fp.squawk = this.nextSquawk();
    return { k: 'CLEARANCE', dest: fp.dest, sid: sid.name, altFt: sid.initialAltFt, squawk: fp.squawk };
  }

  /** Suggested "via" list for a taxi instruction (shortest permitted route). */
  suggestTaxiVia(p: Pilot, target: Extract<Cmd, { k: 'TAXI' }>['target']): string[] | null {
    const ac = p.ac, ap = this.airport;
    const start = this.groundStartNode(ac);
    let to: number | null = null;
    if (target.kind === 'HOLD') to = ap.depHold(target.rwy, target.hold)?.node ?? null;
    else if (target.kind === 'STAND') to = ap.stands[target.stand]?.node ?? null;
    else to = target.node;
    if (start === null || to === null) return null;
    const stands = [ac.stand?.id, target.kind === 'STAND' ? target.stand : undefined].filter((s): s is string => !!s);
    const path = ap.findPath(start, to, { span: ac.type.spanM, operator: ac.fp.airline, stands });
    if (!path) return null;
    const names = ap.routeNames(path);
    // the holding point's own taxiway is implied by "holding point X"
    if (target.kind === 'HOLD' && names[names.length - 1] === target.hold && names.length > 1) names.pop();
    return names;
  }

  /** Directed edges ('a>b') still to be travelled by other aircraft on the ground, plus their stationary positions. */
  private groundPlans(except: Aircraft): { dirs: Set<string>; edges: Set<number>; blocked: Set<number> } {
    const ap = this.airport;
    const dirs = new Set<string>(), edges = new Set<number>(), blocked = new Set<number>();
    for (const o of this.aircraft) {
      if (o === except || !o.onGround || o.gmode === 'PARKED' || o.gmode === 'TAKEOFF' || o.gmode === 'ROLLOUT') continue;
      if (o.atEnd || !o.path.length || o.gmode === 'VACATED' || o.gmode === 'READY' || o.gmode === 'STARTUP') {
        blocked.add(ap.nearestNode(o));
        continue;
      }
      // remaining path of the aircraft (ti counts the leading current-position point)
      const rest = o.path.slice(Math.max(0, o.ti - 2));
      for (let i = 0; i < rest.length - 1; i++) {
        const e = ap.edgeBetween(rest[i]!, rest[i + 1]!);
        if (!e) continue;
        dirs.add(`${rest[i]}>${rest[i + 1]}`);
        edges.add(e.id);
      }
    }
    return { dirs, edges, blocked };
  }

  /** Does a planned ground path meet other traffic head-on or run into a stationary aircraft? */
  groundPathConflicts(ac: Aircraft, path: number[]): boolean {
    const { dirs, blocked } = this.groundPlans(ac);
    for (let i = 0; i < path.length - 1; i++) if (dirs.has(`${path[i + 1]}>${path[i]}`)) return true;
    return path.slice(1).some(n => blocked.has(n));
  }

  /**
   * Route the AI ground controller gives: the shortest permitted route, or – if that meets other traffic head-on –
   * an alternative avoiding the busy taxiways. Returns null when the aircraft should wait.
   */
  aiTaxiRoute(p: Pilot, target: Extract<Cmd, { k: 'TAXI' }>['target']): string[] | null {
    const ac = p.ac, ap = this.airport;
    const start = this.groundStartNode(ac);
    let to: number | null = null;
    if (target.kind === 'HOLD') to = ap.depHold(target.rwy, target.hold)?.node ?? null;
    else if (target.kind === 'STAND') to = ap.stands[target.stand]?.node ?? null;
    else to = target.node;
    if (start === null || to === null) return null;
    const stands = [ac.stand?.id, target.kind === 'STAND' ? target.stand : undefined].filter((s): s is string => !!s);
    const opts = { span: ac.type.spanM, operator: ac.fp.airline, stands };
    let path = ap.findPath(start, to, opts);
    if (path && this.groundPathConflicts(ac, path)) {
      const { edges } = this.groundPlans(ac);
      const closed = new Set(ap.edges.filter(e => edges.has(e.id)));
      const alt = ap.findPath(start, to, { ...opts, closedEdges: closed });
      path = alt && !this.groundPathConflicts(ac, alt) && ap.pathLength(alt) < ap.pathLength(path) * 2.2 ? alt : null;
    }
    if (!path) return null;
    this.plannedPaths.set(ac.cs, path);
    const names = ap.routeNames(path);
    if (target.kind === 'HOLD' && names[names.length - 1] === target.hold && names.length > 1) names.pop();
    return names;
  }
  /** Exact routes behind the AI ground controller's last taxi instructions. */
  private plannedPaths = new Map<string, number[]>();

  /** A free stand that suits the aircraft (span, general aviation). */
  freeStandFor(ac: Aircraft): string | null {
    const ga = !ac.fp.telephony;
    const cands = Object.values(this.airport.stands).filter(s => !this.standOcc.has(s.id) && s.maxSpan >= ac.type.spanM && (ga ? true : !s.ga));
    if (!cands.length) return null;
    cands.sort((a, b) => (ga ? Number(b.ga) - Number(a.ga) : 0) || a.maxSpan - b.maxSpan);
    const pick = cands[Math.floor(this.rng.next() * Math.min(4, cands.length))]!;
    this.standOcc.set(pick.id, ac.cs);
    return pick.id;
  }

  addHoldShort(p: Pilot, rwy: string): void {
    const ac = p.ac, ap = this.airport;
    const rw = ap.runwayEnds[rwy]?.runway ?? ap.runways.find(r => r.id === rwy);
    if (!rw || !ac.path.length) return;
    for (const cr of ap.runwayCrossings(ac.path)) if (cr.runway === rw) ac.stops.set(Math.max(1, cr.index), `RWY:${rw.id}`);
    p.crossings.delete(rw.endIds[0]); p.crossings.delete(rw.endIds[1]);
  }

  startLineup(p: Pilot, rwy: string, holdName?: string): void {
    const ac = p.ac, ap = this.airport;
    const end = ap.runwayEnds[rwy];
    if (!end) return;
    let hold = holdName ? ap.depHold(rwy, holdName) : p.depHold;
    if (!hold || !hold.lineup[rwy]) hold = p.depHold && p.depHold.lineup[rwy] ? p.depHold : end.depHolds[0] ?? null;
    if (!hold) return;
    // route from the current position to the holding point, then onto the runway
    const start = this.groundStartNode(ac);
    let path: number[] = [];
    if (start !== null && start !== hold.node) path = ap.findPath(start, hold.node, { span: ac.type.spanM, operator: ac.fp.airline }) ?? [];
    const lineup = hold.lineup[rwy]!;
    const full = [...path.slice(0, -1), ...lineup];
    p.depHold = hold; p.depRwy = rwy;
    ac.startLineup(ap, end, full.length ? full : lineup);
  }

  // ===================================================================== ground traffic awareness
  /** Distance to the next aircraft in front on the ground path (pilots keep their distance). */
  private groundTraffic(): void {
    const movers = this.aircraft.filter(a => a.onGround && ['TAXI', 'PUSHBACK', 'VACATING', 'LINEUP'].includes(a.gmode));
    const ground = this.aircraft.filter(a => a.onGround);
    const centre = (a: Aircraft): XY => ({ x: a.x - Math.sin(a.hdg * DEG) * a.type.lengthM * 0.45, y: a.y - Math.cos(a.hdg * DEG) * a.type.lengthM * 0.45 });
    const conflicts = new Map<Aircraft, { other: Aircraft; d: number }>();
    for (const a of movers) {
      a.trafficAhead = Infinity;
      a.trafficCs = null;
      if (a.gmode === 'PUSHBACK') continue;
      // sample the path ahead
      let acc = 0;
      let prev: XY = { x: a.x, y: a.y };
      const samples: { p: XY; d: number }[] = [];
      for (let k = a.ti; k < a.pts.length && acc < 140; k++) {
        const q = a.pts[k]!;
        const seg = dist(prev.x, prev.y, q.x, q.y);
        const n = Math.max(1, Math.ceil(seg / 10));
        for (let s = 1; s <= n; s++) {
          const pt = { x: prev.x + (q.x - prev.x) * (s / n), y: prev.y + (q.y - prev.y) * (s / n) };
          samples.push({ p: pt, d: acc + (seg * s) / n });
        }
        acc += seg; prev = q;
      }
      for (const b of ground) {
        if (b === a) continue;
        // aircraft parked on their stand keep the taxilanes clear (stand design)
        if (b.gmode === 'PARKED') continue;
        const cb = centre(b);
        const need = (a.type.spanM + b.type.spanM) / 2 * 0.55 + b.type.lengthM * 0.35 + 8;
        for (const s of samples) {
          if (dist(s.p.x, s.p.y, cb.x, cb.y) < need) {
            const cur = conflicts.get(a);
            if (!cur || s.d < cur.d) conflicts.set(a, { other: b, d: s.d });
            break;
          }
        }
      }
    }
    for (const [a, c] of conflicts) {
      const back = conflicts.get(c.other);
      // mutual: when crossing, the aircraft further from the conflict gives way; head-on both stop
      if (back && back.other === a) {
        const headOn = Math.abs(angDiff(a.hdg, c.other.hdg)) > 130;
        const aYields = headOn || c.d > back.d || (Math.abs(c.d - back.d) < 5 && hashStr(a.cs) > hashStr(c.other.cs));
        if (!aYields) continue;
      }
      a.trafficAhead = c.d;
      a.trafficCs = c.other.cs;
    }
  }

  // ===================================================================== radar
  private radarUpdate(): void {
    if (this.t - this.radarT < 4) return;
    this.radarT = this.t;
    for (const ac of this.aircraft) {
      if (!ac.onGround) {
        ac.history.unshift({ x: ac.radar.x, y: ac.radar.y });
        if (ac.history.length > 6) ac.history.pop();
      }
      ac.radar = { x: ac.x, y: ac.y, alt: ac.altFt, gs: ac.gs, trk: ac.trk, t: this.t };
    }
  }

  // ===================================================================== runway state
  /** Aircraft occupying a runway (on the ground in the strip, or below 300 ft above it). */
  runwayOccupants(rwyId: string): Aircraft[] {
    const rw = this.airport.runways.find(r => r.id === rwyId) ?? this.airport.runwayEnds[rwyId]?.runway;
    if (!rw) return [];
    return this.aircraft.filter(a => {
      if (a.onGround) return this.airport.onRunway(rw, a, 0) && a.gmode !== 'PARKED';
      return a.altFt - this.meta.elevFt < 300 && this.airport.onRunway(rw, a, 30);
    });
  }

  changeAtis(): void {
    const cfg = this.meta.configs.find(c => c.id === this.config.id)!;
    this.tl = transitionLevel(this.meta, this.weather.qnh);
    this.lvp = lvpActive(this.weather);
    this.atis = atis(this.meta, this.weather, cfg, nextAtisLetter(this.atis.letter), this.utc(), this.tl, this.lvp);
  }

  /** Arrival sequence on final for a runway end: aircraft with their distance (NM), closest first. */
  finalSequence(end: RunwayEnd): { ac: Aircraft; d: number }[] {
    return this.aircraft
      .filter(a => !a.onGround && this.pilots.get(a.cs)?.arrRwy === end.id && this.pilots.get(a.cs)?.stage !== 'GONE')
      .map(a => ({ ac: a, d: dist(a.x, a.y, end.thr.x, end.thr.y) / NM }))
      .sort((a, b) => a.d - b.d);
  }

  isDepartureRunway(end: string): boolean { return this.config.dep.includes(end); }
}

export function requestRole(kind: RequestKind): Role | null {
  switch (kind) {
    case 'CLEARANCE': return 'DEL';
    case 'PUSH': case 'STARTUP': case 'TAXI': case 'VACATED': return 'GND';
    case 'DEPARTURE': case 'CHECKIN_FINAL': case 'LANDING': return 'TWR';
    case 'CHECKIN_DEP': return 'DEP';
    case 'CHECKIN_ARR': return 'APP';
    default: return null;
  }
}
